import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import { contactFields } from "../../../../constants/forms/contactFields";
import Form from "../../../../components/forms/Form";
import {
  setDefaultFieldValues,
  cleanPayload,
  normalizeFormErrors,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditCustomerContactPage = () => {
  const navigate = useNavigate();
  const { customerId, contactId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  // useBaseService agora expõe setFormErrors para lidar com erros de validação da API
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(contactFields),
  );

  // --- Query para buscar os detalhes do Contato ---
  const {
    data: contactData,
    isLoading: isLoadingContact,
    isError: isErrorContact,
    error: contactQueryError,
  } = useQueryWithLoader({
    queryKey: ["customerContact", customerId, contactId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.customers.customerContact(customerId, contactId),
      );
      return response.result;
    },
    enabled: !!customerId && !!contactId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (contactData) {
      setFormData({
        contact: contactData,
      });
      hideLoader();
    }
  }, [contactData]);

  // --- Mutação para atualizar o Contato ---
  const updateContactMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.customers.customerContactUpdate(customerId, contactId),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["customerContact", customerId, contactId],
        });
        queryClient.invalidateQueries({
          queryKey: ["customerContacts", customerId],
        });
        navigate(`/aluguel/clientes/detalhes/${customerId}`, {
          state: { type: "success" },
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        contact: {
          ...cleanedData.contact,
          phone: cleanedData.contact.phone
            ? String(cleanedData.contact.phone)
            : null,
          cell: cleanedData.contact.cell
            ? String(cleanedData.contact.cell)
            : null,
        },
      };
      await updateContactMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao atualizar contato:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateContactMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/clientes/detalhes/${customerId}`);
  }, [customerId, navigate]);

  // --- Efeito para exibir notificações de estado da rota (PADRÃO EditUnitPage) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário (PADRÃO EditUnitPage) ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return contactFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Contato do Cliente"
        showBackButton={true}
        backUrl={`/aluguel/clientes/detalhes/${customerId}`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isLoadingContact || updateContactMutation.isPending}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditCustomerContactPage;
